import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

export default function useChatScroll({
  messages,
  isLoadingMessages,
  isLoadingOlder,
  hasMoreMessages,
  onLoadOlderMessages,
  shouldStickToBottomRef,
  activeUserPhone,
}) {
  const scrollRef = useRef(null);
  const previousScrollHeightRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  // When activeUserPhone changes (user clicked a different chat in user list)
  useEffect(() => {
    previousScrollHeightRef.current = 0;
    if (shouldStickToBottomRef) {
      shouldStickToBottomRef.current = true;
    }
  }, [activeUserPhone, shouldStickToBottomRef]);

  // Layout effect to position scroll to the bottom or preserve position on older messages
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || isLoadingMessages) return;

    if (isLoadingOlder) {
      previousScrollHeightRef.current = el.scrollHeight;
      return;
    }

    if (previousScrollHeightRef.current > 0) {
      const heightDiff = el.scrollHeight - previousScrollHeightRef.current;
      el.scrollTop = heightDiff;
      previousScrollHeightRef.current = 0;
      return;
    }

    if (shouldStickToBottomRef?.current) {
      el.scrollTop = el.scrollHeight;

      // Handle layout passes / asynchronous child renders
      const rAf = requestAnimationFrame(() => {
        if (scrollRef.current && shouldStickToBottomRef?.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
      return () => cancelAnimationFrame(rAf);
    }
  }, [messages, isLoadingMessages, isLoadingOlder, shouldStickToBottomRef]);

  // Keep pinned to bottom when content/images/templates expand
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(() => {
      if (shouldStickToBottomRef?.current && scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, [activeUserPhone, shouldStickToBottomRef]);

  // Handle scroll events
  const handleScroll = (e) => {
    const el = scrollRef.current || e?.target;
    if (!el) return;

    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isNearBottom = distanceToBottom <= 80;

    if (shouldStickToBottomRef) {
      shouldStickToBottomRef.current = isNearBottom;
    }

    if (isLoadingMessages || isLoadingOlder || !hasMoreMessages) return;

    // Only load older messages if user scrolled up near top and is NOT near bottom
    if (el.scrollTop <= 40 && !isNearBottom) {
      onLoadOlderMessages?.();
    }
  };

  return { scrollRef, handleScroll, scrollToBottom };
}

